import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FixedToast from '../../components/ui/FixedToast';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile, useFixedToast } from '../../hooks';
import {
  saveBookingCredentials,
  activateBookingProvider,
  clearBookingCredentials,
  updateUserProfile,
  getBillingPlans,
  createCatalogCheckoutSession,
  getUserCallStats,
} from '../../lib/api';
import VoiceSampleCard from './components/VoiceSampleCard';
import ConfigurationForm from './components/ConfigurationForm';
import CallScheduleSettings from './components/CallScheduleSettings';
import DailyCallsSlider from './components/DailyCallsSlider';
import CalendlyIntegration from './components/CalendlyIntegration';
import ExtraMinutesOffer from '../billing/components/ExtraMinutesOffer';
import MonthlyMinutesBar from './components/MonthlyMinutesBar';
import { validateAgentConfiguration } from '../../lib/utils/agentConfigurationValidation';
import { planHasVoiceAI } from '../../lib/utils/planEntitlements';
import { TRIAL_VOICE_AI_MINUTES } from '../../lib/utils/planLimits';
import { mergeExtraMinutesWithStripePrices } from '../../lib/utils/billingPlans';
import { DEFAULT_VAPI_AGENT_ID, VOICE_OPTIONS } from './constants/voiceOptions';
import { getVoiceIdFromAssistant } from '../../lib/api/vapi';

const AIAssistantSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile, loading: profileLoading, refetch } = useProfile();
  const { toast, showToast, hideToast } = useFixedToast();
  const plan = profile?.subscription_plan?.toLowerCase() || null;
  const hasVoiceAI = planHasVoiceAI(plan);
  const dailyMinutesMax = plan === 'trial' ? TRIAL_VOICE_AI_MINUTES : 100;

  const [selectedVapiAgentId, setSelectedVapiAgentId] = useState(DEFAULT_VAPI_AGENT_ID);
  const [isSavingVoice, setIsSavingVoice] = useState(false);
  const [maxDailyCalls, setMaxDailyCalls] = useState(100);
  const [isSavingConfiguration, setIsSavingConfiguration] = useState(false);
  const [isSavingMaxDailyCalls, setIsSavingMaxDailyCalls] = useState(false);
  const [isSavingBooking, setIsSavingBooking] = useState(false);
  const [isActivatingBooking, setIsActivatingBooking] = useState(false);
  const [isClearingBooking, setIsClearingBooking] = useState(false);

  const [extraMinutesProducts, setExtraMinutesProducts] = useState(() => mergeExtraMinutesWithStripePrices());
  const [extraMinutesLoading, setExtraMinutesLoading] = useState(true);
  const [buyingExtraId, setBuyingExtraId] = useState('');
  const [callStats, setCallStats] = useState({ calls_duration: 0, minutes_limit: 0 });

  const extraMinutes = Math.max(0, Number(profile?.extra_minutes) || 0);

  const [formData, setFormData] = useState({
    companyName: '',
    agentName: '',
    linkedinUrl: '',
  });

  const [errors, setErrors] = useState({});
  const hasInitializedForm = useRef(false);

  useEffect(() => {
    if (!profile || hasInitializedForm.current) return;
    setFormData({
      agentName: profile.ai_agent_name ?? '',
      companyName: profile.company_name ?? '',
      linkedinUrl: profile.linkedin_url ?? '',
    });
    hasInitializedForm.current = true;
  }, [profile]);

  useEffect(() => {
    if (profile?.max_daily_calls !== undefined && profile?.max_daily_calls !== null) {
      const minutes = Number(profile.max_daily_calls);
      setMaxDailyCalls(Math.min(dailyMinutesMax, Math.max(4, minutes)));
    }
  }, [profile?.max_daily_calls, dailyMinutesMax]);

  useEffect(() => {
    if (profile?.vapi_agent_id !== undefined) {
      setSelectedVapiAgentId(profile.vapi_agent_id || DEFAULT_VAPI_AGENT_ID);
    }
  }, [profile?.vapi_agent_id]);

  const [callSchedule, setCallSchedule] = useState({
    timeSlots: ['morning', 'afternoon'],
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    respectTimezone: true
  });

  const voiceSamples = VOICE_OPTIONS;

  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [voiceDurations, setVoiceDurations] = useState({});
  const audioRef = useRef(null);

  const formatDuration = (seconds) => {
    if (seconds == null || isNaN(seconds)) return null;
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!hasVoiceAI || !user?.id) return;
    let cancelled = false;
    getUserCallStats(user.id)
      .then((stats) => {
        if (!cancelled) setCallStats(stats || { calls_duration: 0, minutes_limit: 0 });
      })
      .catch((err) => {
        console.error('Failed to load call stats:', err);
      });
    return () => { cancelled = true; };
  }, [hasVoiceAI, user?.id]);

  useEffect(() => {
    if (!hasVoiceAI) return;
    let cancelled = false;
    setExtraMinutesLoading(true);
    getBillingPlans()
      .then(({ prices }) => {
        if (!cancelled) setExtraMinutesProducts(mergeExtraMinutesWithStripePrices(prices));
      })
      .catch(() => {
        if (!cancelled) setExtraMinutesProducts(mergeExtraMinutesWithStripePrices());
      })
      .finally(() => {
        if (!cancelled) setExtraMinutesLoading(false);
      });
    return () => { cancelled = true; };
  }, [hasVoiceAI]);

  useEffect(() => {
    if (!hasVoiceAI) return;

    voiceSamples
      .filter((voice) => voice.sampleUrl)
      .forEach((voice) => {
        const audio = new Audio();
        audio.addEventListener('loadedmetadata', () => {
          setVoiceDurations((prev) => ({ ...prev, [voice.id]: audio.duration }));
        });
        audio.src = voice.sampleUrl;
      });
  }, [hasVoiceAI]);

  if (profileLoading && !profile) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('ai.loadingSettings')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!hasVoiceAI) {
    return (
      <MainLayout>
        <div className="space-y-6 md:space-y-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">{t('ai.setupTitle')}</h1>
              <p className="text-sm md:text-base text-muted-foreground">{t('ai.setupSubtitle')}</p>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-6 md:p-8 text-center">
            <p className="text-foreground font-medium mb-2">{t('ai.upgradeTitle')}</p>
            <p className="text-muted-foreground mb-6">
              {t('ai.upgradeBody')}
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Button asChild>
                <Link to="/billing" className="inline-flex items-center gap-2">
                  <Icon name="CreditCard" size={18} />
                  {t('common.upgradePlan')}
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/dashboard" className="inline-flex items-center gap-2">
                  <Icon name="ArrowLeft" size={18} />
                  {t('common.backToDashboard')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSaveConfiguration = async () => {
    if (!user?.id) return;

    const validation = validateAgentConfiguration(formData);
    setErrors(validation.errors);
    if (!validation.isValid) return;

    setIsSavingConfiguration(true);
    try {
      await updateUserProfile(user.id, {
        ai_agent_name: validation.values.agentName,
        company_name: validation.values.companyName,
        linkedin_url: validation.values.linkedinUrl,
      });
      setFormData((prev) => ({
        ...prev,
        agentName: validation.values.agentName,
        companyName: validation.values.companyName,
        linkedinUrl: validation.values.linkedinUrl,
      }));
      await refetch();
      showToast();
    } catch (err) {
      console.error('Failed to save AI agent configuration:', err);
    } finally {
      setIsSavingConfiguration(false);
    }
  };

  const handleSaveMaxDailyCalls = async (value) => {
    if (!user?.id) return;
    setIsSavingMaxDailyCalls(true);
    try {
      await updateUserProfile(user.id, {
        max_daily_calls: Math.min(dailyMinutesMax, Math.max(4, Number(value) || 0)),
      });
      await refetch();
      showToast();
    } catch (err) {
      console.error('Failed to save max daily calls:', err);
    } finally {
      setIsSavingMaxDailyCalls(false);
    }
  };

  const handleBuyExtraMinutes = async ({ product, price }) => {
    if (!price?.priceId) return;
    setBuyingExtraId(product.id);
    try {
      const { url } = await createCatalogCheckoutSession({
        productId: product.id,
        priceId: price.priceId,
        returnPath: '/ai-assistant/setup',
      });
      window.location.href = url;
    } catch (err) {
      console.error('Failed to start extra minutes checkout:', err);
      setBuyingExtraId('');
    }
  };

  const handlePlayVoice = (voice) => {
    const url = voice?.sampleUrl;
    if (!url) return;

    if (playingVoiceId === voice?.id) {
      if (isPaused) {
        audioRef.current?.play();
        setIsPaused(false);
      } else {
        audioRef.current?.pause();
        setIsPaused(true);
      }
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPaused(false);
    setPlayingVoiceId(voice?.id);
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onloadedmetadata = () => {
      setVoiceDurations((prev) => ({ ...prev, [voice?.id]: audio.duration }));
    };
    audio.onended = () => {
      setPlayingVoiceId(null);
      setIsPaused(false);
      audioRef.current = null;
    };
    audio.onerror = () => {
      setPlayingVoiceId(null);
      setIsPaused(false);
      audioRef.current = null;
    };
    audio.play().catch(() => {
      setPlayingVoiceId(null);
      setIsPaused(false);
      audioRef.current = null;
    });
  };

  const handleSelectVoice = async (voice) => {
    const vapiAgentId = voice?.vapiAgentId;
    if (!vapiAgentId || !user?.id || isSavingVoice) return;
    if (vapiAgentId === selectedVapiAgentId) return;

    setSelectedVapiAgentId(vapiAgentId);
    setIsSavingVoice(true);

    try {
      const voiceId = await getVoiceIdFromAssistant(vapiAgentId);
      await updateUserProfile(user.id, { vapi_agent_id: vapiAgentId, voice_id: voiceId });
      await refetch();
      showToast();
    } catch (err) {
      console.error('Failed to save voice:', err);
      setSelectedVapiAgentId(profile?.vapi_agent_id || DEFAULT_VAPI_AGENT_ID);
    } finally {
      setIsSavingVoice(false);
    }
  };

  const handleBookingSave = async (payload) => {
    if (!user?.id) return;
    setIsSavingBooking(true);
    try {
      await saveBookingCredentials(user.id, payload);
      await refetch();
      showToast();
    } catch (err) {
      console.error('Failed to save booking credentials:', err);
    } finally {
      setIsSavingBooking(false);
    }
  };

  const handleBookingActivate = async (payload) => {
    if (!user?.id) return;
    setIsActivatingBooking(true);
    try {
      // Teams / Google tokens are written by OAuth callbacks (n8n), not the form.
      if (payload.bookingType !== 'teams' && payload.bookingType !== 'google') {
        await saveBookingCredentials(user.id, payload);
      }
      await activateBookingProvider(user.id, payload.bookingType);
      await refetch();
      showToast();
    } catch (err) {
      console.error('Failed to activate booking provider:', err);
    } finally {
      setIsActivatingBooking(false);
    }
  };

  const handleOAuthSuccess = async (bookingType) => {
    if (!user?.id || !bookingType) return;
    setIsActivatingBooking(true);
    try {
      await activateBookingProvider(user.id, bookingType);
      await refetch();
      showToast();
    } catch (err) {
      console.error('Failed to activate provider after OAuth connect:', err);
      await refetch();
    } finally {
      setIsActivatingBooking(false);
    }
  };

  const handleBookingClear = async (bookingType) => {
    if (!user?.id) return;
    setIsClearingBooking(true);
    try {
      await clearBookingCredentials(user.id, bookingType, profile?.booking_type ?? null);
      await refetch();
      showToast();
    } catch (err) {
      console.error('Failed to clear booking credentials:', err);
    } finally {
      setIsClearingBooking(false);
    }
  };

  return (
    <MainLayout>
      <FixedToast
        isOpen={toast.open}
        message={toast.message}
        variant={toast.variant}
        onClose={hideToast}
      />
      <div className="space-y-6 md:space-y-8 py-6 md:py-8">
        <div className="mb-6 md:mb-8">
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">{t('ai.setupTitle')}</h1>
              <p className="text-sm md:text-base text-muted-foreground">{t('ai.setupSubtitle')}</p>
            </div>
          </div>

          <div>
            <div className="mb-4 md:mb-6">
              <h2 className="text-lg md:text-xl font-semibold text-foreground mb-2">{t('ai.voiceSelection')}</h2>
              <p className="text-sm text-muted-foreground">{t('ai.voiceSelectionHint')}</p>
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
              {voiceSamples?.map((voice) => (
                <VoiceSampleCard
                  key={voice?.id}
                  voice={voice}
                  duration={formatDuration(voiceDurations[voice?.id])}
                  isSelected={selectedVapiAgentId === voice?.vapiAgentId}
                  isPlaying={playingVoiceId === voice?.id && !isPaused}
                  isPaused={playingVoiceId === voice?.id && isPaused}
                  onSelect={() => handleSelectVoice(voice)}
                  onPlay={() => handlePlayVoice(voice)}
                />
              ))}
            </div>
          </div>

          <ConfigurationForm
            formData={formData}
            errors={errors}
            onChange={handleFormChange}
            onSave={handleSaveConfiguration}
            isSaving={isSavingConfiguration}
          />

          <DailyCallsSlider
            value={maxDailyCalls}
            onChange={setMaxDailyCalls}
            min={4}
            max={dailyMinutesMax}
            onSave={handleSaveMaxDailyCalls}
            isSaving={isSavingMaxDailyCalls}
            headerRight={
              <MonthlyMinutesBar
                used={callStats.calls_duration || 0}
                total={(callStats.minutes_limit || 0) + extraMinutes}
              />
            }
          />

          <div className="rounded-xl border border-border bg-card p-4 md:p-6">
            <ExtraMinutesOffer
              products={extraMinutesProducts}
              loading={extraMinutesLoading}
              selectingId={buyingExtraId}
              onBuy={handleBuyExtraMinutes}
            />
          </div>

          {/* <CallScheduleSettings
            schedule={callSchedule}
            onScheduleChange={setCallSchedule}
          /> */}

          <CalendlyIntegration
            profile={profile}
            onSave={handleBookingSave}
            onActivate={handleBookingActivate}
            onClear={handleBookingClear}
            onRefetch={refetch}
            onOAuthSuccess={handleOAuthSuccess}
            isSaving={isSavingBooking}
            isActivating={isActivatingBooking}
            isClearing={isClearingBooking}
          />
      </div>
    </MainLayout>
  );
};

export default AIAssistantSettings;